/** @jsxImportSource @emotion/react */
import Input from 'components/atoms/Input'
import React, { useEffect, useMemo, useRef, useState } from 'react'

export interface Option {
  label: string
  value: string
}

export interface SearchableInputProps {
  options: Option[]
  value: string
  onChange: (val: string) => void
  onBlur: () => void
  placeholder?: string
  className?: string
}

const SearchableInput: React.FC<SearchableInputProps> = ({
  options,
  value,
  onChange,
  onBlur,
  placeholder = 'Type or choose…',
  className,
}) => {
  const [inputValue, setInputValue] = useState(value)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => setInputValue(value), [value])

  const filteredOptions = useMemo(() => {
    if (!inputValue) return options
    const query = inputValue.toLowerCase()
    return options.filter(
      (o) => o.label.toLowerCase().includes(query) || o.value.toLowerCase().includes(query),
    )
  }, [inputValue, options])

  const handleChange = (val: string) => {
    setInputValue(val)
    onChange(val)
    setIsOpen(true)
  }

  const handleSelect = (opt: Option) => {
    setInputValue(opt.value)
    onChange(opt.value)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className={className} style={{ position: 'relative', width: '100%' }}>
      <Input
        type="text"
        value={inputValue}
        placeholder={placeholder}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          setIsOpen(false)
          onBlur()
        }}
        changeOnBlur={false}
        handleChange={(e) => handleChange(e.target.value)}
      />

      {isOpen && filteredOptions.length > 0 && (
        <ul
          css={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #ccc',
            borderRadius: 5,
            background: '#fff',
            marginTop: 2,
            padding: 0,
            listStyle: 'none',
            zIndex: 1000,
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
          }}>
          {filteredOptions.map((opt) => (
            <li
              key={opt.value}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(opt)}
              css={{ padding: '8px', cursor: 'pointer', '&:hover': { background: '#f0f0f0' } }}>
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default SearchableInput
